import { useState, useEffect } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDate } from '../utils/dateFormatter';
import './UserManagement.css';

import { UserRole } from '../contexts/AuthContext';

interface User {
  id: number;
  username: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

interface UserPermission {
  section: string;
  can_view?: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  can_export?: boolean;
  can_import?: boolean;
  can_adjust_stock?: boolean;
  can_approve?: boolean;
  can_view_reports?: boolean;
  can_manage_settings?: boolean;
}

interface PermissionSection {
  id: string;
  label: string;
  category: string;
}

const PERMISSION_SECTIONS: PermissionSection[] = [
  // Principal
  { id: 'dashboard', label: 'Dashboard', category: 'Principal' },
  { id: 'assistant', label: 'Asistente IA', category: 'Principal' },
  { id: 'scheduled-reports', label: 'Reportes Programados', category: 'Principal' },
  
  // Inventario
  { id: 'stock', label: 'Stock', category: 'Inventario' },
  { id: 'movements', label: 'Movimientos de Stock', category: 'Inventario' },
  { id: 'metal-pieces', label: 'Gestión Piezas', category: 'Inventario' },
  { id: 'stones', label: 'Gestión Piedras', category: 'Inventario' },
  { id: 'development-pieces', label: 'Piezas en Desarrollo', category: 'Inventario' },
  
  // Producción
  { id: 'work-orders', label: 'Órdenes de Fabricación', category: 'Producción' },
  { id: 'fundicion', label: 'Fundición', category: 'Producción' },
  { id: 'revestimiento', label: 'Revestimiento', category: 'Producción' },
  { id: 'montaje', label: 'Montaje', category: 'Producción' },
  { id: '3pl', label: '3PL', category: 'Producción' },
  { id: 'soporte', label: 'Soporte', category: 'Producción' },
  
  // Compras
  { id: 'smart_stock', label: 'Control Inteligente de Stock', category: 'Compras' },
  { id: 'piedras-pedidos', label: 'Pedidos Piedras', category: 'Compras' },
  { id: 'stone-consumption', label: 'Consumo Piedras', category: 'Compras' },
  { id: 'boxes', label: 'Estuches & Packaging', category: 'Compras' },
  
  // Ventas
  { id: 'ventas', label: 'Importar Ventas', category: 'Ventas' },
  { id: 'ventas-stats', label: 'Estadísticas Ventas', category: 'Ventas' },
  { id: 'order-preparation', label: 'Control Pedidos', category: 'Ventas' },
  
  // Configuración
  { id: 'min-stock-metal', label: 'Establecer mínimos - Piezas', category: 'Configuración' },
  { id: 'min-stock-stones', label: 'Establecer mínimos - Piedras', category: 'Configuración' },
  { id: 'bom', label: 'BOM desde SKU', category: 'Configuración' },
  { id: 'settings', label: 'Configuración', category: 'Configuración' },
  { id: 'users', label: 'Gestión de Usuarios', category: 'Configuración' },
  { id: 'business-rules', label: 'Reglas de Negocio', category: 'Configuración' },
  { id: 'admin-log', label: 'Log de Administración', category: 'Configuración' },
];

export default function UserManagement() {
  const { api } = useApi();
  const { isAdmin, user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios pueden cargar usuarios
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadUserPermissions(selectedUser.id);
    }
  }, [selectedUser]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      setUsers(response.data);
    } catch (error: any) {
      showToast('Error al cargar usuarios', 'error');
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/auth/users/${userId}/permissions`);
      const userPermissions: UserPermission[] = response.data;
      
      // Crear un mapa de permisos existentes
      const permissionsMap = new Map<string, UserPermission>();
      userPermissions.forEach(p => {
        permissionsMap.set(p.section, p);
      });

      // Crear array completo con todas las secciones
      const allPermissions: UserPermission[] = PERMISSION_SECTIONS.map(section => {
        const existing = permissionsMap.get(section.id);
        return existing || {
          section: section.id,
          can_view: false,
          can_edit: false,
          can_delete: false,
          can_export: false,
          can_import: false,
          can_adjust_stock: false,
          can_approve: false,
          can_view_reports: false,
          can_manage_settings: false,
        };
      });

      setPermissions(allPermissions);
    } catch (error: any) {
      showToast('Error al cargar permisos', 'error');
      console.error('Error al cargar permisos:', error);
    } finally {
      setLoading(false);
    }
  };

  type PermissionField = 'can_view' | 'can_edit' | 'can_delete' | 'can_export' | 'can_import' | 'can_adjust_stock' | 'can_approve' | 'can_view_reports' | 'can_manage_settings';

  const handlePermissionChange = (section: string, field: PermissionField, value: boolean) => {
    setPermissions(prev => 
      prev.map(p => 
        p.section === section 
          ? { ...p, [field]: value }
          : p
      )
    );
  };

  // Definir qué acciones están disponibles para cada sección
  const getAvailableActions = (sectionId: string): { field: PermissionField; label: string; requires?: PermissionField }[] => {
    const baseActions = [
      { field: 'can_view' as PermissionField, label: 'Ver' },
      { field: 'can_edit' as PermissionField, label: 'Editar', requires: 'can_view' as PermissionField },
    ];

    // Acciones específicas por sección
    const sectionActions: Record<string, { field: PermissionField; label: string; requires?: PermissionField }[]> = {
      'stock': [
        { field: 'can_adjust_stock', label: 'Ajustar Stock' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'movements': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'metal-pieces': [
        { field: 'can_delete', label: 'Eliminar' },
        { field: 'can_export', label: 'Exportar' },
        { field: 'can_import', label: 'Importar' },
      ],
      'stones': [
        { field: 'can_delete', label: 'Eliminar' },
        { field: 'can_export', label: 'Exportar' },
        { field: 'can_import', label: 'Importar' },
      ],
      'development-pieces': [
        { field: 'can_delete', label: 'Eliminar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'work-orders': [
        { field: 'can_approve', label: 'Aprobar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'fundicion': [
        { field: 'can_approve', label: 'Aprobar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'revestimiento': [
        { field: 'can_approve', label: 'Aprobar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'montaje': [
        { field: 'can_approve', label: 'Aprobar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      '3pl': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'soporte': [
        { field: 'can_approve', label: 'Resolver Tickets' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'smart_stock': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'piedras-pedidos': [
        { field: 'can_approve', label: 'Aprobar' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'stone-consumption': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'boxes': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'ventas': [
        { field: 'can_import', label: 'Importar' },
        { field: 'can_export', label: 'Exportar' },
        { field: 'can_view_reports', label: 'Ver Reportes' },
      ],
      'ventas-stats': [
        { field: 'can_view_reports', label: 'Ver Reportes' },
        { field: 'can_export', label: 'Exportar' },
      ],
      'order-preparation': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'scheduled-reports': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'min-stock-metal': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'min-stock-stones': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'bom': [
        { field: 'can_export', label: 'Exportar' },
        { field: 'can_import', label: 'Importar' },
      ],
      'settings': [
        { field: 'can_manage_settings', label: 'Gestionar Configuración' },
      ],
      'users': [
        { field: 'can_manage_settings', label: 'Gestionar Usuarios' },
      ],
      'business-rules': [
        { field: 'can_export', label: 'Exportar' },
      ],
      'admin-log': [
        { field: 'can_export', label: 'Exportar' },
      ],
    };

    return [...baseActions, ...(sectionActions[sectionId] || [])];
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      // Enviar todos los permisos, incluso los que están en false
      // El backend manejará correctamente los valores por defecto
      await api.put(`/auth/users/${selectedUser.id}/permissions`, permissions);
      showToast('Permisos actualizados correctamente', 'success');
      await loadUserPermissions(selectedUser.id);
      
      // Si el usuario actual es el que se está editando, recargar sus permisos
      if (currentUser?.id === selectedUser.id) {
        // Disparar evento para que AuthContext recargue los permisos
        window.dispatchEvent(new CustomEvent('permissions-updated'));
      }
    } catch (error: any) {
      showToast('Error al guardar permisos', 'error');
      console.error('Error al guardar permisos:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleUserStatus = async (user: User) => {
    try {
      setLoading(true);
      await api.put(`/auth/users/${user.id}`, {
        is_active: !user.is_active,
      });
      showToast(`Usuario ${user.is_active ? 'desactivado' : 'activado'} correctamente`, 'success');
      await loadUsers();
      if (selectedUser?.id === user.id) {
        setSelectedUser({ ...user, is_active: !user.is_active });
      }
    } catch (error: any) {
      showToast('Error al actualizar usuario', 'error');
      console.error('Error al actualizar usuario:', error);
    } finally {
      setLoading(false);
    }
  };

  // SISTEMA DE PERMISOS DESHABILITADO - Todos los usuarios tienen acceso

  const groupedSections = PERMISSION_SECTIONS.reduce((acc, section) => {
    if (!acc[section.category]) {
      acc[section.category] = [];
    }
    acc[section.category].push(section);
    return acc;
  }, {} as Record<string, PermissionSection[]>);

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>Gestión de Usuarios y Permisos</h1>
        <p>Administra usuarios y asigna permisos por sección</p>
      </div>

      <div className="user-management-content">
        <div className="user-list-panel">
          <h2>Usuarios</h2>
          {loading && !selectedUser ? (
            <div className="loading">Cargando usuarios...</div>
          ) : (
            <div className="user-list">
              {users.map(user => (
                <div
                  key={user.id}
                  className={`user-item ${selectedUser?.id === user.id ? 'selected' : ''} ${!user.is_active ? 'inactive' : ''}`}
                  onClick={() => setSelectedUser(user)}
                >
                  <div className="user-item-header">
                    <span className="user-name">{user.username}</span>
                    <span className={`user-role ${user.role.toLowerCase()}`}>
                      {user.role === 'ADMINISTRADOR' ? 'Admin' : 
                       user.role === 'SUPERVISOR' ? 'Supervisor' :
                       user.role === 'OPERADOR' ? 'Operador' :
                       user.role === 'LECTOR' ? 'Lector' :
                       user.role === 'GESTOR' ? 'Gestor' : 'Usuario'}
                    </span>
                  </div>
                  <div className="user-item-details">
                    <span className={`user-status ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                    {user.last_login && (
                      <span className="user-last-login">
                        Último acceso: {formatDate(user.last_login)}
                      </span>
                    )}
                  </div>
                  <button
                    className="toggle-user-status"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleUserStatus(user);
                    }}
                    disabled={loading}
                  >
                    {user.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="permissions-panel">
          {selectedUser ? (
            <>
              <div className="permissions-header">
                <h2>Permisos de {selectedUser.username}</h2>
                {selectedUser.role === 'ADMINISTRADOR' && (
                  <div className="admin-notice">
                    Los administradores tienen todos los permisos automáticamente.
                  </div>
                )}
              </div>

              {selectedUser.role !== 'ADMINISTRADOR' ? (
                <>
                  {loading ? (
                    <div className="loading">Cargando permisos...</div>
                  ) : (
                    <>
                      <div className="permissions-list">
                        {Object.entries(groupedSections).map(([category, sections]) => (
                          <div key={category} className="permission-category">
                            <h3 className="permission-category-title">{category}</h3>
                            {sections.map(section => {
                              const permission = permissions.find(p => p.section === section.id);
                              if (!permission) return null;

                              const availableActions = getAvailableActions(section.id);

                              return (
                                <div key={section.id} className="permission-item">
                                  <div className="permission-label">
                                    <span>{section.label}</span>
                                  </div>
                                  <div className="permission-controls">
                                    {availableActions.map(action => {
                                      const isChecked = permission[action.field] || false;
                                      const isDisabled = action.requires && !(permission[action.requires] || false);
                                      
                                      return (
                                        <label 
                                          key={action.field} 
                                          className="permission-checkbox"
                                          style={{ 
                                            opacity: isDisabled ? 0.5 : 1,
                                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={(e) =>
                                              handlePermissionChange(section.id, action.field, e.target.checked)
                                            }
                                            disabled={isDisabled}
                                          />
                                          <span>{action.label}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      <div className="permissions-actions">
                        <button
                          className="save-permissions-btn btn btn-primary"
                          onClick={handleSavePermissions}
                          disabled={saving || loading}
                        >
                          {saving ? 'Guardando...' : '💾 Guardar Permisos'}
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="admin-permissions-info">
                  <p>Los administradores tienen acceso completo a todas las secciones.</p>
                </div>
              )}
            </>
          ) : (
            <div className="no-user-selected">
              <p>Selecciona un usuario para gestionar sus permisos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

